// ProfileDetailsUpload.js
import React, { useState, useMemo, useRef, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
// Assuming Ant Design is installed and its styles are loaded.
import { Table, Tabs, Badge, Tooltip } from "antd";
import Papa from "papaparse";
import readXlsxFile from "read-excel-file";
import customFetch from "../apicall/api";
// NOTE: Make sure the CSS file path is correct:
import styles from "./styles/FileUpload.module.css";
import DynamicForm from "../DynamicForm";
// --- FileUpload Component ---


// --- Helper: Generate AntD columns from config columns ---
function getAntdColumns(columns, errorColumns = []) {
  return columns.map(col => ({
    title: col,
    dataIndex: col,
    key: col,
    className: errorColumns.includes(col) ? styles.errorColumn : "",
    render: (text, record) => {
      if (record && record.__colErrors && record.__colErrors[col]) {
        return (
          <Tooltip title={record.__colErrors[col]}>
            <span className={styles.errorCell}>{text}</span>
          </Tooltip>
        );
      }
      return text;
    }
  }));
}

// --- File Select Modal Component (with config-based accept) ---
const FileSelectModal = ({
  isVisible,
  onClose,
  fileSelections,
  setFileSelections,
  setUploadedFiles,
  onFileUpload,
  requiredFileSlots,
  onDownloadSample,
  downloadingSlotId
}) => {
  if (!isVisible) return null;

  const handleSlotClick = slotId => {
    document.getElementById(`fileInput-${slotId}`).click();
  };

  const handleFileChange = (event, slotId) => {
    const file = event.target.files[0];
    if (file) {
      onFileUpload(file, slotId);
      event.target.value = null;
    }
  };

  // NEW HANDLER: Stops event propagation for the hyperlink download
  const handleDownloadClick = (e, slotId) => {
      e.stopPropagation(); // Stops click from triggering the parent's handleSlotClick
      if (downloadingSlotId) return;
      onDownloadSample(slotId);
  };
  
  const filesSelectedCount = Object.keys(fileSelections).length;

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modalContent}>
        <h3 className={styles.modalTitle}>Select Required Files</h3>
        <div className={styles.requiredFilesList}>
          {requiredFileSlots.map(slot => {
            const isCurrentSlotDownloading = downloadingSlotId === slot.id;
            return (
            <div
              key={slot.id}
              className={styles.requiredFileSlot}
              onClick={() => handleSlotClick(slot.id)}
              role="button"
              tabIndex={0}
            >
              <div className={styles.slotInfo}>
                <div className={styles.slotName}>
                  {slot.name}{" "}
                  {slot.mandatory && <span className={styles.required}>*</span>}
                </div>
                <div className={styles.fileAcceptsContainer}>
                    <span className={styles.slotExtension}>
                        Accepts: {slot.extension}
                    </span>
                    {/* The Download Link */}
                    <a 
                        href="#" 
                        className={`${styles.downloadSampleLink} ${downloadingSlotId ? styles.disabledLink : ''}`}
                        onClick={(e) => handleDownloadClick(e, slot.id)} // Use the new handler
                    >
                        {isCurrentSlotDownloading ? 'Downloading...' : '(Download Sample)'}
                    </a>
                </div>
              </div>
              <div className={styles.fileSelectionStatus}>
                {fileSelections[slot.id] ? (
                  <div className={styles.fileStatusReady}>
                    ✔ {fileSelections[slot.id].name}
                  </div>
                ) : (
                  <div className={styles.fileStatusPending}>
                    Click to Select File
                  </div>
                )}
              </div>
              <input
                type="file"
                id={`fileInput-${slot.id}`}
                className={styles.hiddenFileInput}
                onChange={e => handleFileChange(e, slot.id)}
                accept={slot.extension}
              />
            </div>
          )
        })}
        </div>
        <div className={styles.modalActions}>
          <button
            className={`${styles.actionButton} ${styles.uploadButton}`}
            onClick={onClose}
            disabled={filesSelectedCount === 0}
          >
            Done ({filesSelectedCount} files)
          </button>
          <button
            className={`${styles.actionButton} ${styles.cancelButton}`}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
const ProfileDetailsUpload = () => {
  const location = useLocation();
  const [showFileModal, setShowFileModal] = useState(false);
  
  // form config
  const [reportConfig, setReportConfig] = useState();
  const [reportId, setReportId] = useState();
  const [errorWindowMessage, setErrorWindowMessage] = useState("");
  // {"partner":{"value":"Acen","isVisible":true,"options":[""]},"vendor_name":{"value":"digio","isVisible":true}}
  const [formState, setFormState] = useState(null);
  const [formHeader, setFormHeader] = useState("");
  const [isFormLoading, setIsFormLoading] = useState(false);

  // Config and slots
  const [config, setConfig] = useState(null);
  const [requiredFileSlots, setRequiredFileSlots] = useState([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState("");

  // File state: { [slotId]: { id, name, status, slotId, parsedData, columns, errors, missingColumns } }
  const [fileSelections, setFileSelections] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [activeTab, setActiveTab] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [downloadingSlotId, setDownloadingSlotId] = useState(null);
  const history = useHistory();
  const [tablePageSize, setTablePageSize] = useState(5);

  const { api_token, effective_designation_id } = location.state || {};
  const final_api_token = api_token || localStorage.getItem("api_token");

  useEffect(()=>{
    console.log("report config is updated", reportConfig);
  },[reportConfig])
  
  // Fetch config from API based on profile_key from navigation state
  useEffect(() => {
    async function fetchConfig() {
      setLoadingConfig(true);
      setConfigError("");
      let profileKey = location.state.profile_key;
      if (!profileKey) {
        setConfigError("No profile selected.");
        setLoadingConfig(false);
        return;
      }
      try {
        const params = new URLSearchParams({
          profile_key: profileKey,
          effective_designation_id: effective_designation_id
        });
        
        // const response = await fetch(`http://localhost:5000/bulk-file-upload/upload-configs?${params.toString()}`, {
        //   method: "GET",
        //   headers: {"api-token": final_api_token },
        // });

        const response = await customFetch(
          `${process.env.REACT_APP_URL}/bulk-file-upload/upload-configs?${params.toString()}`,
          { method: "GET",
            headers: {"api-token": final_api_token }
          }
        );
        const data = await response.json();
        if (data.status === 1 && data.data) {
          setConfig(data.data);
          // setFormIdReference(data.data.form_config.form_id || "");
          // Build slots from config
          const slots = (data.data.files_config || []).map(cfg => ({
            id: cfg.file_code,
            name: cfg.description || cfg.file_code,
            extension: (cfg.allowed_extensions || [])
              .map(ext => "." + ext)
              .join(", "),
            columns: cfg.columns || [],
            mandatory: cfg.mandatory,
            allowed_extensions: cfg.allowed_extensions || [],
            file_index: cfg.file_index,
            sample_download_url: cfg.sample_download_url
          }));
          setRequiredFileSlots(slots);
          console.log("nut", data)
          setFormHeader(data.data.form_config.description || "");
          setReportId(data.data.form_config.form_id || "");
        } else {
          setConfigError(data.message || "Config not found.");
        }
      } catch (err) {
        setConfigError("Failed to fetch config.");
        console.log("nut",err)
      }
      setLoadingConfig(false);
    }
    fetchConfig();
  }, [location.state]);

  // --- File Parsing and Validation ---
  const parseAndValidateFile = (file, slotId) => {
    const slotConfig = requiredFileSlots.find(slot => slot.id === slotId);
    if (!slotConfig) return;
    const allowedExts =
      slotConfig.allowed_extensions ||
      slotConfig.extension.split(",").map(e => e.replace(".", "").trim());
    const ext = file.name
      .split(".")
      .pop()
      .toLowerCase();

    const handleParsed = (data, columns) => {
      // Validation: check required columns
      const requiredCols = slotConfig.columns || [];
      const missingColumns = requiredCols.filter(col => !columns.includes(col));
      // Mark errors for missing columns
      const errors = {};
      if (missingColumns.length > 0) {
        missingColumns.forEach(col => {
          errors[col] = "Missing required column";
        });
      }
      // Row-level error marking for missing columns
      const dataWithErrors = data.map((row, idx) => {
        const rowErrors = {};
        missingColumns.forEach(col => {
          rowErrors[col] = "Missing required column";
        });
        return { key: idx, ...row, __colErrors: rowErrors };
      });
      const fileIndex = slotConfig.file_index

      const newFile = {
        id: Date.now(),
        name: file.name,
        status: "Ready",
        slotId,
        parsedData: dataWithErrors,
        columns,
        errors,
        missingColumns,
        fileIndex,
        fileObject: file
      };

      setFileSelections(prev => ({
        ...prev,
        [slotId]: newFile
      }));

      setUploadedFiles(prev => {
        const filesWithoutOldSlot = prev.filter(f => f.slotId !== slotId);
        return [...filesWithoutOldSlot, newFile];
      });

      // Set active tab to this file
      setActiveTab(slotId);
    };

    if (ext === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: result => {
          const columns = result.meta.fields || [];
          handleParsed(result.data, columns);
        }
      });
    } else if (ext === "xlsx") {
      readXlsxFile(file).then(rows => {
        if (!rows.length) return handleParsed([], []);
        const columns = rows[0];
        const data = rows.slice(1).map((row, idx) => {
          const obj = {};
          columns.forEach((col, i) => {
            obj[col] = row[i];
          });
          return obj;
        });
        handleParsed(data, columns);
      });
    }
  };

  // --- Tabs for File Preview ---
  const tabItems = useMemo(() => {
    return uploadedFiles.map(file => {
      const slotConfig = requiredFileSlots.find(
        slot => slot.id === file.slotId
      );
      const columns = slotConfig.columns.length
        ? slotConfig.columns
        : file.columns || [];
      const antdColumns = getAntdColumns(columns, file.missingColumns || []);
      const hasErrors = file.missingColumns && file.missingColumns.length > 0;

      // Limit the data source to the first 50 rows ---
      const limitedParsedData = (file.parsedData || []).slice(0, 50);

      // Validation status
      let validationStatus = "Passed";
      let statusIcon = "✔";
      if (hasErrors) {
        validationStatus = "Missing Columns";
        statusIcon = "⚠️";
      } else if (!limitedParsedData || !limitedParsedData.length) {
        validationStatus = "No Data";
        statusIcon = "—";
      }

      return {
        key: file.slotId,
        label: (
          <Badge
            count={hasErrors ? <span className={styles.errorBadge}>!</span> : 0}
            offset={[8, 0]}
            size="small"
          >
            {file.name}
          </Badge>
        ),
        children: (
          <div className={styles.tabContent}>
            <div className={styles.validationHeader}>
              Validation Status:
              <span
                className={
                  hasErrors
                    ? styles.validationStatusError
                    : styles.validationStatus
                }
              >
                <span className={styles.statusDot}>{statusIcon}</span>
                {validationStatus}
              </span>
              {hasErrors && (
                <span className={styles.validationErrorMsg}>
                  [{file.missingColumns.join(", ")}]
                </span>
              )}
            </div>
            <div className={styles.tableContainer}>
              <Table
                columns={antdColumns}
                dataSource={limitedParsedData}
                pagination={{ 
                  pageSize: tablePageSize,
                  onChange: (page, size) =>{
                    if (size !== tablePageSize) {
                      setTablePageSize(size); 
                      // You might also want to reset the current page back to 1 here
                    }
                  },
                  showSizeChanger: true, 
                  pageSizeOptions: [5, 10, 20, 50] // Define options for the user
                 }}
                size="small"
                scroll={{ x: "max-content" }}
                rowClassName={row =>
                  row.__colErrors &&
                  Object.values(row.__colErrors).some(Boolean)
                    ? styles.errorRow
                    : ""
                }
              />
            </div>
          </div>
        )
      };
    });
  }, [uploadedFiles, requiredFileSlots, tablePageSize]);

  // Set the initial active tab to the first file's slotId, or null
  useEffect(() => {
    if (uploadedFiles.length && !activeTab) {
      setActiveTab(uploadedFiles[0].slotId);
    }
  }, [uploadedFiles, activeTab]);

  // --- File Upload Handler ---
  const handleFileUpload = (file, slotId) => {
    parseAndValidateFile(file, slotId);
  };


  const uploadFilesToGCS = async (localFiles, apiResponse) => {
    if (apiResponse.status !== 1 || !apiResponse.file_url_data) {
        throw new Error("Invalid API response provided for GCS upload.");
    }
    
    // Create a map for quick lookup: file_index -> URL data
    const urlMap = apiResponse.file_url_data.reduce((map, item) => {
        map[item.file_index] = item;
        return map;
    }, {});

    // Create an array of Promises, one for each file upload
    const uploadPromises = localFiles.map(async (file, index) => {
        const urlData = urlMap[index];

        // Basic check to ensure we have a URL for the current file index
        if (!urlData || !urlData.upload_url) {
            console.error(`Skipping file at index ${index}: No presigned URL found.`);
            return { index, filename: file.name, success: false, status: 'NO_URL' };
        }
        
        const uploadUrl = urlData.upload_url;
        const contentType = file.fileObject.type; // Get the file's MIME type
        
        console.log(`Starting upload for file ${file.name} to ${uploadUrl.substring(0, 80)}...`);

        try {
            // The GCS presigned URL requires a PUT request.
            // Content-Type header is crucial and must match the one signed by the backend.
            const response = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': contentType,
                    // No need for 'api-token' or 'Authorization' since the signature is in the URL
                },
                body: file.fileObject, // Send the raw File object as the body
            });

            if (response.ok) {
                console.log(`Successfully uploaded file ${file.name}.`);
                return { index, filename: file.name, success: true, status: response.status, systemFilename: urlData.system_generated_filename };
            } else {
                // If GCS returns an error (e.g., 403 Forbidden, 400 Bad Request)
                const errorText = await response.text().catch(() => `Error ${response.status} with GCS.`);
                console.error(`Failed GCS upload for ${file.name}: ${errorText}`);
                return { index, filename: file.name, success: false, status: response.status, error: errorText };
            }
        } catch (error) {
            console.error(`Network or critical error uploading ${file.name}:`, error);
            return { index, filename: file.name, success: false, status: 'NETWORK_ERROR', error: error.message };
        }
    });

    // Run all uploads concurrently and wait for all to complete
    const results = await Promise.all(uploadPromises);
    
    // Filter out successful results for the next step (e.g., notifying the backend)
    const successfulUploads = results.filter(r => r.success);
    
    console.log("All file uploads attempted. Successful:", successfulUploads.length, "Failed:", results.length - successfulUploads.length);
    
    return results;
  };

  const updateFileUploadStatus = async (uploadRequestId, fileIndex, status) => {
    try {
        const payload = {
            upload_request_id: uploadRequestId,
            file_index: fileIndex, // Pass null if updating root status
            status: status
        };
        
        // const response = await fetch(`http://localhost:5000/bulk-file-upload/update/file/status`, {
        //     method: "POST",
        //     headers: { 
        //         "Content-Type": "application/json", 
        //         "api-token": localStorage.getItem("api_token") 
        //     },
        //     body: JSON.stringify(payload),
        // });

        const response = await customFetch(
          process.env.REACT_APP_URL + `/bulk-file-upload/update/file/status`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json", 
              "api-token": final_api_token,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => "");
            console.error(`Status update failed for ID ${uploadRequestId} (Index: ${fileIndex}): ${response.status} ${errorText}`);
            throw new Error(`Failed to update status: ${response.status} ${errorText}`);
        }

        const data = await response.json();
        if (data.status === 1) {
            console.log(`Successfully updated status for ID ${uploadRequestId} (Index: ${fileIndex}) to '${status}'.`);
        }
        return data;

    } catch (error) {
        console.error("API error during file status update:", error);
        // Do not re-throw here, just log and allow the main process to continue
        return { status: 0, message: `Update failed due to network error: ${error.message}` };
    }
  };

  const generatePresignedUrl = async (uploadedFiles, profileKey, formState, userId) => {
    
    const dynamicFormData = {};
    let isFormValid = true;

    // Iterate over all keys in the formState object
    for (const key in formState) {
        if (formState.hasOwnProperty(key)) {
            const formField = formState[key];
            // Check if the field is visible (optional, but good practice if your state tracks visibility)
            if (formField && formField.isVisible !== false) {
                // Extract id and value
                const id = formField.id;
                const value = formField.value;
                // Validate: Check if id and value are present and non-empty for visible fields
                if (id === null || id === undefined || id === "" || value === null || value === undefined || value === "") {
                    // Set flag to false and break the loop immediately
                    isFormValid = false;
                    console.error(`Validation Failed: Form field '${key}' is missing ID or Value.`);
                    break; 
                }
                // Add the clean, extracted object to the dynamic data container
                dynamicFormData[key] = {
                    id: id,
                    name: value // Using 'name' for consistency with previous objects
                };
            }
        }
    }
    // --- Block API call if form is invalid ---
    if (!isFormValid) {
        setIsUploading(false); // Stop loader
        // You should show a user-friendly error here (e.g., using AntD Message or a Modal)
        alert("Please select a value for all required form fields before uploading."); 
        return;
    }
    if (!uploadedFiles || uploadedFiles.length === 0) {
        setIsUploading(false); // Ensure loader is off
        alert("Please select files to upload."); 
        return; 
    }

    setIsUploading(true);
    try {
      const fileDataArray = uploadedFiles.map((file) => {
          const filename = file.name; 
          const fileindex = file.fileIndex
          const mimeType = file.fileObject.type;
          return {
            filename:filename,
            file_index:fileindex,
            content_type: mimeType
          }
      });

      const payload = {
        file_process_type: profileKey,
        file_data: fileDataArray,
        uploaded_by: userId,
        form_data: dynamicFormData
      };

      // const response =  await fetch(`http://localhost:5000/bulk-file-upload/generate/presigned/urls`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json", "api-token": localStorage.getItem("api_token") },
      //   body: JSON.stringify(payload),
      // });

      const response = await customFetch(
        process.env.REACT_APP_URL + `/bulk-file-upload/generate/presigned/urls`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", 
            "api-token": localStorage.getItem("api_token"),
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(`Failed to fetch data: ${response.status} ${text}`);
      }

      const data = await response.json();

      if (data.status !== 1) {
        throw new Error("Backend failed to generate presigned URLs.");
      }

      // --- Capture the unique ID for later status updates ---
      const uploadRequestId = data.upload_request_id;
      console.log("Presigned URLs generated. Upload Request ID:", uploadRequestId);

      // --- STEP 2: Upload Files to GCS ---
      const uploadResults = await uploadFilesToGCS(uploadedFiles, data);
      console.log("File Upload Results:", uploadResults);


      // --- STEP 3: Update Status in MongoDB ---
      if (uploadRequestId) {
          const successfulUploads = uploadResults.filter(r => r.success);
          const allFilesSuccessful = successfulUploads.length === uploadedFiles.length;

          // A. Update status for each successfully uploaded file
          const statusUpdatePromises = successfulUploads.map(r => 
              updateFileUploadStatus(uploadRequestId, r.index, 'file_uploaded')
          );

          // B. Update status for files that failed
          const failedUploads = uploadResults.filter(r => !r.success);
            failedUploads.forEach(r => {
              // You might use a different status like 'file_upload_failed' 
              // but for this example, we focus on the success path.
              updateFileUploadStatus(uploadRequestId, r.index, 'file_upload_failed');
          });
          
          // Wait for all individual status updates to complete
          await Promise.all(statusUpdatePromises);

          // C. If ALL files were uploaded successfully, update the ROOT status
          if (allFilesSuccessful) {
              console.log("All files uploaded successfully. Updating root status.");
              await updateFileUploadStatus(uploadRequestId, null, 'file_uploaded'); // file_index: null for root update
          }
          history.push({
          pathname:'/bulk-file-upload/upload-history',
          state: { api_token: localStorage.getItem("api_token"), effective_designation_id: effective_designation_id }
        });
      }
      
      // You can return the upload results or any other useful data here
      return { uploadResults, uploadRequestId };

      } catch (error) {
        console.error("Critical error in generatePresignedUrl:", error);

        return { records: [], pagination: { current: 1, pageSize: 10, total: 0 } };
      }finally {
          // END: Reset loading state regardless of success or failure
          setIsUploading(false);
      }
  };

  // Function to handle the download of a single sample file from GCS
  const downloadSampleFile = async (slotId) => {

      if (downloadingSlotId === slotId) return;
      // 1. Find the required slot config to get the file code/name for the backend
      const slotConfig = requiredFileSlots.find(slot => slot.id === slotId);
      if (!slotConfig) {
          alert("Configuration error: Sample file details not found.");
          return;
      }

      setDownloadingSlotId(slotId);
      try {
          const payload = {
              file_process_type: location.state.profile_key,
              sample_download_path: slotConfig.sample_download_url,
          };

          // const response = await fetch(`http://localhost:5000/bulk-file-upload/generate/download/urls`, 
          // { method: 'POST', 
          //   headers:{
          //     "api-token": localStorage.getItem("api_token"),
          //     "Content-Type": "application/json",
          //   },
          //   body: JSON.stringify(payload),
          // });

          const response = await customFetch(
            process.env.REACT_APP_URL + `/bulk-file-upload/generate/download/urls`,
            {
              method: "POST",
              headers: {
                "api-token": localStorage.getItem("api_token"),
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          if (!response.ok) {
              throw new Error(`Failed to get signed URL: ${response.status}`);
          }

          const data = await response.json();
          const signedUrl = data.download_url; 

          if (!signedUrl) {
              throw new Error("Backend did not return a valid signed URL.");
          }

          // 3. Trigger the browser download
          const a = document.createElement('a');
          a.href = signedUrl;
          // The default filename provided by GCS is usually sufficient, 
          // but you can suggest a name here:
          // a.download = `${slotConfig.name.replace(/[^a-z0-9]/gi, '_')}_Sample.${slotConfig.allowed_extensions[0] || 'csv'}`; 
          document.body.appendChild(a);
          a.click();
          a.remove();

      } catch (error) {
          console.error("Error downloading sample file:", error);
          alert(`Failed to download sample for ${slotConfig.name}. Please try again.`);
      } finally {
        setDownloadingSlotId(null); // END LOADER
      }
  };


  return (
    <div className={styles.container}>
      <div className={styles.header}>{location.state.profile_name || ""}</div>

      {loadingConfig ? (
        <div className={styles.loading}>Loading config...</div>
      ) : configError ? (
        <div className={styles.error}>{configError}</div>
      ) : (
        <>
          {
            reportId && !isFormLoading &&
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>
                {formHeader}
              </h4>
              <DynamicForm
                reportConfig={reportConfig} setReportConfig={setReportConfig}
                formState={formState} setFormState={setFormState}
                errorWindowMessage={errorWindowMessage} setErrorWindowMessage={setErrorWindowMessage}
                currentReportId={reportId}
                isFormLoading={isFormLoading}
                setIsFormLoading={setIsFormLoading}
              />
            </div>
          }
            

          {/* File Preview using AntD Tabs */}
          <div className={styles.section}>
            <div className={styles.flexContainer}>
              <h3 className={styles.sectionTitle}>File Preview</h3>
              {/* <div className={styles.fileSummary}>
                {uploadedFiles.length > 0 ? (
                  <>
                    <span className={styles.summaryIcon}>📁</span>
                    {uploadedFiles.length} files selected, ready for upload.
                  </>
                ) : (
                  "No files selected."
                )}
              </div>*/}
              <div className={styles.fileSelectControl}>
                <button
                  className={`${styles.actionButton} ${styles.draftButton}`}
                  onClick={() => setShowFileModal(true)}
                >
                  Select Files ({uploadedFiles.length})
                </button>
              </div>
            </div>
            {uploadedFiles.length > 0 ? (
              <Tabs
                defaultActiveKey={uploadedFiles[0].slotId}
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                type="card"
                className={styles.filePreviewTabs}
              />
            ) : (
              <div className={styles.noFileMessage}>
                Please use the "Select Files" button above to choose files for
                preview.
              </div>
            )}
          </div>

          <div className={styles.footerActions}>
            {/* <button className={`${styles.actionButton} ${styles.cancelButton}`}>
              Cancel
            </button>*/}
            <button
              onClick={() => {
                // Gather validation errors from uploadedFiles
                const validationErrors = [];
                uploadedFiles.forEach(file => {
                  if (file.missingColumns && file.missingColumns.length > 0) {
                    validationErrors.push(
                      `File "${file.name}" is missing columns: ${file.missingColumns.join(", ")}`
                    );
                  }
                });

                if (validationErrors.length > 0) {
                  alert(validationErrors.join('\n'));
                } else {
                  // Log selected files and form state along with profile_key
                  console.log({
                    selectedFiles: uploadedFiles,
                    formState,
                    profile_key: location.state.profile_key,
                    in_userid: localStorage.getItem("in_userid")
                  });
                  generatePresignedUrl(uploadedFiles, location.state.profile_key, formState, localStorage.getItem("in_userid"))
                }
              }}
              className={`${styles.actionButton} ${styles.uploadButton}`}
              disabled={isUploading}
              
              
            >
              {isUploading ? 'Uploading...' : 'Upload Now'}
            </button>
          </div>

          <FileSelectModal
            isVisible={showFileModal}
            onClose={() => setShowFileModal(false)}
            fileSelections={fileSelections}
            setFileSelections={setFileSelections}
            setUploadedFiles={setUploadedFiles}
            onFileUpload={handleFileUpload}
            requiredFileSlots={requiredFileSlots}
            onDownloadSample={downloadSampleFile}
            downloadingSlotId={downloadingSlotId}
          />
        </>
      )}
    </div>
  );
};

export default ProfileDetailsUpload;
